import React, { useEffect, useMemo, useState } from 'react';
import { bp_influencers, bp_sources, bp_get_retention_settings } from '../../../services';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import CommonPagination from '../../../Components/CommonPagination';
import CommonTable from '../../../Components/CommonTable';
import CommonTabs from '../../../Components/CommonTabs';
import NoDataPage from '../../../Components/NoDataPage';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import AddOnCard from '../../AddOns/AddOnCard';
import { lightTheme as T } from '../../../Constants/Colors';
import { SubHeading, Body } from '../../../Constants/Fonts';
import { unwrap } from './_shared';
import {
    PlatformAvatarDisc, AccountAvatar,
    PlatformChip, profileUrlFor,
} from './_avatarKit';
import { Icon } from './_icons';
import ModuleIcon from '../../../Components/ModuleIcon';
import PostListModal, { openPostsFor } from './PostListModal';
import './brand-mentions.scss';

const SUB_TABS = ['influencers', 'sources'];

const DAYS_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
];

const SORT_KEYS = [
    { value: 'mentions', label: 'Mentions' },
    { value: 'neg_share', label: 'Negative share' },
];
const SORT_OPTIONS = [
    { value: 'mentions:desc', label: 'Sort: Most mentions' },
    { value: 'mentions:asc', label: 'Sort: Fewest mentions' },
    { value: 'neg_share:desc', label: 'Sort: Most negative' },
    { value: 'neg_share:asc', label: 'Sort: Least negative' },
];

const PAGE_SIZE = 10;

const INFLUENCER_HEADERS = ['#', 'Influencer', 'Platform', 'Mentions', 'Share of voice', 'Sentiment', 'Risk'];
const SOURCE_HEADERS = ['#', 'Platform', 'Mentions', 'Share'];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function SentimentMeter({ sentiment, total }) {
    const s = sentiment || {};
    const pos = s.positive || 0, neg = s.negative || 0;
    const neu = s.neutral != null ? s.neutral : Math.max(0, (total || 0) - pos - neg);
    const sum = pos + neu + neg || 1;
    const pct = n => `${(n / sum) * 100}%`;
    return (
        <span className="bm_is_sent" title={`${pos} positive · ${neu} neutral · ${neg} negative`}>
            <span className="bm_is_sent_bar">
                {pos > 0 && <span className="seg pos" style={{ width: pct(pos) }} />}
                {neu > 0 && <span className="seg neu" style={{ width: pct(neu) }} />}
                {neg > 0 && <span className="seg neg" style={{ width: pct(neg) }} />}
            </span>
            {neg > 0
                ? <Body as="span" variant={2} weight="semibold" className="bm_is_sent_label is-negative">{neg} neg</Body>
                : <Body as="span" variant={2} className="bm_is_sent_label is-clean">clean</Body>}
        </span>
    );
}

function ProfileLink({ i, label = '↗' }) {
    const url = i.name_masked ? null : profileUrlFor(i.handle, i.platform);
    if (!url) return null;
    return (
        <Body as="a" variant={2} href={url} target="_blank" rel="noopener noreferrer"
            className="bm_is_profile_link"
            title={`Open @${i.handle} on ${i.platform}`}
            onClick={e => e.stopPropagation()}>
            {label}
        </Body>
    );
}

function postsTitle(i) {
    return i.mentions === 1
        ? `Open @${i.handle}'s post on ${i.platform}`
        : `See @${i.handle}'s ${i.mentions || 0} posts on ${i.platform}`;
}

function TopVoices({ items, onOpen }) {
    const top3 = items.slice(0, 3);
    return (
        <div className="bm_is_podium">
            {top3.map((i, idx) => (
                <AddOnCard key={(i.handle || '') + (i.platform || '')}
                    className={`bm_is_podium_card is-rank-${idx + 1}`}
                    align="flex-start"
                    onClick={() => onOpen(i)}
                    title={postsTitle(i)}
                    iconBg="transparent"
                    iconNode={<AccountAvatar handle={i.handle} platform={i.platform}
                        handleToken={i.handle_token} size={40} />}
                    label={
                        <div className="bm_is_podium_body">
                            <div className="bm_is_podium_name">
                                <SubHeading as="span" variant={3} className="bm_is_handle" title={i.handle}>
                                    {i.handle}
                                </SubHeading>
                                <ProfileLink i={i} />
                            </div>
                            <div className="bm_is_podium_meta">
                                <PlatformChip platform={i.platform} />
                                <Body as="span" variant={2} className="bm_is_muted">
                                    {plural(i.mentions || 0, 'mention')} · {(i.share_of_voice || 0).toFixed(1)}% share of voice
                                </Body>
                            </div>
                            <SentimentMeter sentiment={i.sentiment} total={i.mentions} />
                        </div>
                    }
                    action={
                        <div className="bm_is_podium_badges">
                            <StatusBadge variant={idx === 0 ? 'primary' : 'neutral'} dot={false}>
                                #{idx + 1}
                            </StatusBadge>
                            {i.risk_tagged && (
                                <StatusBadge variant="danger" dot={false} transform="uppercase">
                                    Risk source
                                </StatusBadge>
                            )}
                        </div>
                    }
                />
            ))}
            {[...Array(Math.max(0, 3 - top3.length))].map((_, idx) => (
                <AddOnCard key={`empty-${idx}`}
                    className="bm_is_podium_card is-placeholder"
                    label={<Body as="span" variant={2} className="bm_is_muted">Awaiting more voices</Body>}
                    action={null}
                />
            ))}
        </div>
    );
}

function Panel({ title, note, children }) {
    return (
        <section className="bm_is_panel cmn_elevated_card">
            {(title || note) && (
                <div className="bm_is_panel_head">
                    {title && <SubHeading as="h3" variant={2} className="bm_is_panel_title">{title}</SubHeading>}
                    {note && <Body as="span" variant={2} className="bm_is_muted">{note}</Body>}
                </div>
            )}
            {children}
        </section>
    );
}

function Pager({ page, pageCount, onChange }) {
    if (pageCount <= 1) return null;
    return (
        <div className="bm_comments_pagination">
            <CommonPagination
                handlePageClick={({ selected }) => onChange(selected)}
                forcePage={page}
                pageCount={pageCount}
            />
        </div>
    );
}

export default function InfluencersTab() {
    const [days, setDays] = useState(30);
    const [subTab, setSubTab] = useState('influencers');
    const [influencers, setInfluencers] = useState([]);
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortKey, setSortKey] = useState('mentions');
    const [sortDir, setSortDir] = useState('desc');
    const [infPage, setInfPage] = useState(0);
    const [srcPage, setSrcPage] = useState(0);
    const [retentionDays, setRetentionDays] = useState(null);
    const [postTarget, setPostTarget] = useState(null);
    const openInfluencer = i => openPostsFor(
        { kind: 'author', handle: i.handle, platform: i.platform }, days, i.mentions, setPostTarget);
    const openSource = src => openPostsFor(
        { kind: 'platform', platform: src.platform }, days, src.mentions, setPostTarget);

    useEffect(() => {
        let alive = true;
        bp_get_retention_settings()
            .then(res => { if (alive) setRetentionDays(unwrap(res)?.retention_days ?? null); })
            .catch(() => { /* retention unknown - leave all ranges available */ });
        return () => { alive = false; };
    }, []);

    // Date-range options can't exceed the org's data-retention window: mentions
    // older than `retentionDays` are purged, so a longer range returns nothing.
    // Hide any option longer than retention (keep-forever = null/0 keeps every
    // range), and clamp the current selection if it's now too long.
    const dayOptions = useMemo(() => {
        if (!retentionDays) return DAYS_OPTIONS;
        const withinRetention = DAYS_OPTIONS.filter(o => o.value <= retentionDays);
        return withinRetention.length ? withinRetention : [DAYS_OPTIONS[0]];
    }, [retentionDays]);

    useEffect(() => {
        if (retentionDays && days > retentionDays) {
            const maxWithin = dayOptions[dayOptions.length - 1];
            if (maxWithin && maxWithin.value !== days) setDays(maxWithin.value);
        }
    }, [retentionDays, days, dayOptions]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        Promise.allSettled([
            bp_influencers({ days, limit: 50 }),
            bp_sources({ days }),
        ]).then(([i, s]) => {
            if (!alive) return;
            const ok = r => r?.status === 'fulfilled' ? r.value : null;
            setInfluencers((unwrap(ok(i)) || {}).influencers || []);
            setSources((unwrap(ok(s)) || {}).sources || []);
            setLoading(false);
        });
        return () => { alive = false; };
    }, [days]);

    const totalMentions = useMemo(() =>
        influencers.reduce((s, i) => s + (i.mentions || 0), 0) || 1,
        [influencers]);

    const totalSourceMentions = useMemo(() =>
        sources.reduce((s, x) => s + (x.mentions || 0), 0) || 1,
        [sources]);

    // True only while the user is on the default Mentions/desc sort. The
    // moment they pick a different sort key or flip the direction, we
    // honor their explicit choice instead of pinning risk-tagged rows to
    // the top - otherwise the dropdown looks broken because the visible
    // order can't change.
    const isDefaultSort = sortKey === 'mentions' && sortDir === 'desc';

    const sortedInfluencers = useMemo(() => {
        const withSov = influencers.map(i => ({
            ...i,
            share_of_voice: ((i.mentions || 0) / totalMentions) * 100,
            neg_share: ((i.sentiment?.negative || 0) / Math.max(1, i.mentions || 0)) * 100,
        }));
        const cmp = (a, b) => {
            const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
            return sortDir === 'asc' ? av - bv : bv - av;
        };
        if (isDefaultSort) {
            const risk = withSov.filter(i => i.risk_tagged);
            const rest = withSov.filter(i => !i.risk_tagged);
            risk.sort(cmp); rest.sort(cmp);
            return [...risk, ...rest];
        }
        return withSov.sort(cmp);
    }, [influencers, sortKey, sortDir, totalMentions, isDefaultSort]);

    const sortedSources = useMemo(() => {
        const enriched = sources.map(s => ({
            ...s,
            share_pct: ((s.mentions || 0) / totalSourceMentions) * 100,
        }));
        enriched.sort((a, b) => (b.mentions || 0) - (a.mentions || 0));
        return enriched;
    }, [sources, totalSourceMentions]);

    // Reset pagination whenever the underlying set or ordering changes, so the
    // user never lands on a now-empty page after re-filtering / re-sorting.
    useEffect(() => { setInfPage(0); }, [days, sortKey, sortDir]);
    useEffect(() => { setSrcPage(0); }, [days]);

    const infRows = sortedInfluencers.slice(3);
    const infPageCount = Math.max(1, Math.ceil(infRows.length / PAGE_SIZE));
    const infPageRows = infRows.slice(infPage * PAGE_SIZE, (infPage + 1) * PAGE_SIZE);

    const srcPageCount = Math.max(1, Math.ceil(sortedSources.length / PAGE_SIZE));
    const srcPageRows = sortedSources.slice(srcPage * PAGE_SIZE, (srcPage + 1) * PAGE_SIZE);

    const topVoice = sortedInfluencers[0];
    const topSource = sortedSources[0];
    const riskCount = useMemo(
        () => influencers.filter(i => i.risk_tagged).length,
        [influencers]);
    const sortLabel = SORT_KEYS.find(o => o.value === sortKey)?.label.toLowerCase();

    const influencerRows = infPageRows.map((i, idx) => ({
        item: i,
        data: [
            <Body as="span" variant={2} weight="semibold" className="bm_is_rank">#{infPage * PAGE_SIZE + idx + 4}</Body>,
            <span className="bm_is_identity" title={postsTitle(i)}>
                <AccountAvatar handle={i.handle} platform={i.platform}
                    handleToken={i.handle_token} size={32} />
                <Body as="span" weight="semibold" className="bm_is_handle">{i.handle}</Body>
                <ProfileLink i={i} />
            </span>,
            <span className="bm_is_platform">
                <PlatformChip platform={i.platform} />
                <Body as="span" variant={2} className="bm_is_platform_name">{i.platform}</Body>
            </span>,
            <Body as="span" weight="semibold" className="bm_is_num">{i.mentions || 0}</Body>,
            <Body as="span" className="bm_is_num">{(i.share_of_voice || 0).toFixed(1)}%</Body>,
            <SentimentMeter sentiment={i.sentiment} total={i.mentions} />,
            i.risk_tagged
                ? <StatusBadge variant="danger" dot={false} transform="uppercase"
                    icon={<Icon name="alert" size={10} color={T.danger} />}>Risk</StatusBadge>
                : <Body as="span" variant={2} className="bm_is_muted">-</Body>,
        ],
    }));

    const sourceRows = srcPageRows.map((s, idx) => ({
        item: s,
        data: [
            <Body as="span" variant={2} weight="semibold" className="bm_is_rank">#{srcPage * PAGE_SIZE + idx + 1}</Body>,
            <span className="bm_is_identity"
                title={s.mentions === 1
                    ? `Open the post from ${s.platform}`
                    : `See the ${s.mentions || 0} posts from ${s.platform} and open any on the platform`}>
                <PlatformAvatarDisc platform={s.platform} size={32} />
                <Body as="span" weight="semibold" className="bm_is_platform_name">{s.platform}</Body>
            </span>,
            <Body as="span" weight="semibold" className="bm_is_num">{s.mentions || 0}</Body>,
            <Body as="span" className="bm_is_num">{s.share_pct.toFixed(1)}%</Body>,
        ],
    }));

    return (
        <div className="bm_is">
            <div className="bm_is_toolbar">
                <CommonTabs
                    tabs={[`Influencers (${sortedInfluencers.length})`, `Sources (${sortedSources.length})`]}
                    values={SUB_TABS}
                    active={subTab}
                    handlers={SUB_TABS.map(t => () => setSubTab(t))}
                />
                <div className="bm_is_toolbar_controls">
                    <CommonDropdown
                        ariaLabel="Date range"
                        value={days}
                        onChange={setDays}
                        options={dayOptions}
                    />
                    {subTab === 'influencers' && (
                        <CommonDropdown
                            ariaLabel="Sort by"
                            value={`${sortKey}:${sortDir}`}
                            onChange={v => {
                                const [key, dir] = v.split(':');
                                setSortKey(key);
                                setSortDir(dir);
                            }}
                            options={SORT_OPTIONS}
                            minWidth={200}
                        />
                    )}
                </div>
            </div>

            {loading && (
                <Panel>
                    <div className="bm_is_loading">
                        <CommonLoader label={subTab === 'influencers' ? 'Loading influencers…' : 'Loading sources…'} />
                    </div>
                </Panel>
            )}

            {/* ============ INFLUENCERS ============ */}
            {!loading && subTab === 'influencers' && (
                sortedInfluencers.length === 0 ? (
                    <Panel>
                        <NoDataPage
                            title="No influencers in this window"
                            hint="Influencer rankings populate as the scanner captures mentions. Widen the date range or wait for the next scan cycle."
                        />
                    </Panel>
                ) : (
                    <>
                        <div className="bm_is_kpis">
                            <StatCard className="cmn_elevated_card"
                                label="Total voices"
                                value={sortedInfluencers.length}
                                hint={`${totalMentions} mentions across this set`}
                                icon={<span className="bm_kpi_icon"><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
                            <StatCard className="cmn_elevated_card"
                                label="Top voice"
                                value={topVoice ? `@${(topVoice.handle || '').slice(0, 16)}` : '-'}
                                hint={topVoice
                                    ? `${topVoice.platform} · ${plural(topVoice.mentions || 0, 'mention')}`
                                    : 'no data'}
                                icon={<span className="bm_kpi_icon is-purple"><ModuleIcon name="users" size={18} color="currentColor" /></span>} iconLeft />
                            <StatCard className="cmn_elevated_card"
                                label="Risk-tagged"
                                value={riskCount}
                                valueColor={riskCount > 0 ? T.danger : undefined}
                                hint={riskCount > 0
                                    ? (isDefaultSort ? 'pinned at top of list ↓' : 'marked by RISK SOURCE chip')
                                    : 'none in this window'}
                                icon={<span className={`bm_kpi_icon is-${riskCount > 0 ? 'danger' : 'neutral'}`}><ModuleIcon name="risk" size={18} color="currentColor" /></span>} iconLeft />
                        </div>

                        <Panel title="Top voices" note={`ranked by ${sortLabel}`}>
                            <TopVoices items={sortedInfluencers} onOpen={openInfluencer} />
                        </Panel>

                        {infRows.length > 0 && (
                            <Panel title="All voices" note={`${plural(infRows.length, 'more voice')} · click a row to open the posts`}>
                                <CommonTable
                                    headers={INFLUENCER_HEADERS}
                                    rows={influencerRows}
                                    onRowClick={row => openInfluencer(row.item)}
                                    noScroll
                                />
                                <Pager page={infPage} pageCount={infPageCount} onChange={setInfPage} />
                            </Panel>
                        )}
                    </>
                )
            )}

            {/* ============ SOURCES ============ */}
            {!loading && subTab === 'sources' && (
                sortedSources.length === 0 ? (
                    <Panel>
                        <NoDataPage
                            title="No source platforms in this window"
                            hint="Sources are derived from where mentions originate. Once mentions are captured across X, Reddit, Bluesky, etc., they'll group by platform here."
                        />
                    </Panel>
                ) : (
                    <>
                        <div className="bm_is_kpis">
                            <StatCard className="cmn_elevated_card"
                                label="Platforms hosting"
                                value={sortedSources.length}
                                hint={`${totalSourceMentions} mentions across all platforms`}
                                icon={<span className="bm_kpi_icon"><ModuleIcon name="easm" size={18} color="currentColor" /></span>} iconLeft />
                            <StatCard className="cmn_elevated_card"
                                label="Top platform"
                                value={topSource ? <span className="bm_is_platform_name">{topSource.platform}</span> : '-'}
                                hint={topSource
                                    ? `${topSource.mentions} mentions · ${topSource.share_pct.toFixed(1)}% share`
                                    : 'no data'}
                                icon={<span className="bm_kpi_icon is-purple"><ModuleIcon name="layers" size={18} color="currentColor" /></span>} iconLeft />
                            <StatCard className="cmn_elevated_card"
                                label="Top platform share"
                                value={topSource ? `${topSource.share_pct.toFixed(1)}%` : '-'}
                                hint={topSource ? `${topSource.mentions} of ${totalSourceMentions} mentions` : 'no data'}
                                icon={<span className="bm_kpi_icon"><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
                            <StatCard className="cmn_elevated_card"
                                label="Avg per platform"
                                value={sortedSources.length ? Math.round(totalSourceMentions / sortedSources.length) : '-'}
                                hint="average mentions per platform"
                                icon={<span className="bm_kpi_icon"><ModuleIcon name="score" size={18} color="currentColor" /></span>} iconLeft />
                        </div>

                        <Panel title="Platforms" note="ranked by mentions · click a row to open the posts">
                            <CommonTable
                                headers={SOURCE_HEADERS}
                                rows={sourceRows}
                                onRowClick={row => openSource(row.item)}
                                noScroll
                            />
                            <Pager page={srcPage} pageCount={srcPageCount} onChange={setSrcPage} />
                        </Panel>
                    </>
                )
            )}

            {postTarget && (
                <PostListModal
                    target={postTarget}
                    days={days}
                    onViewInMentions={t => (t.kind === 'author'
                        ? drillIntoMentionsForAuthor(t.handle)
                        : drillIntoMentionsForPlatform(t.platform))}
                    onClose={() => setPostTarget(null)}
                />
            )}
        </div>
    );
}

function drillIntoMentionsForAuthor(handle) {
    window.dispatchEvent(new CustomEvent('bm-mentions-set-filter',
        { detail: { author: handle } }));
    window.dispatchEvent(new CustomEvent('bm-tab-switch',
        { detail: { tab: 'mentions' } }));
}

function drillIntoMentionsForPlatform(platform) {
    window.dispatchEvent(new CustomEvent('bm-mentions-set-filter',
        { detail: { platform } }));
    window.dispatchEvent(new CustomEvent('bm-tab-switch',
        { detail: { tab: 'mentions' } }));
}
